import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, CheckCircle, Download, Info, Package, Percent, Plus, Trash2, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import { TablePaginationBar } from '@/components/shared/TablePaginationBar';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';
import { useWarehouse } from '@/contexts/WarehouseContext';
import { useCurrency } from '@/hooks/useCurrency';
import {
  applyMoqFloorOnCommit,
  getSupplyQuantityInputMin,
  parseSupplyQuantity,
  quantitiesEqual,
  resolveSupplyQuantityAfterStepInteraction,
  stepSupplyQuantityByMp,
} from '@/modules/supply/utils/quantity';
import { resolveSupplyUnitDisplay } from '@/modules/supply/utils/supplyUnitDisplay';
import {
  supplyService,
  type PendingOrder,
  type PurchaseOrderLinePayload,
  type PurchaseOrderPayload,
} from '@/services/supplyService';
import { invalidateSupplyTableCache } from '@/utils/supplyTableCache';

function getPendingOrderPurchaseTypology(productProdType?: string | null): {
  labelKey: string;
  fallback?: string;
} | null {
  const value = (productProdType || '').trim().toUpperCase();
  if (!value || value === 'STD') {
    return null;
  }
  if (value === 'P') {
    return { labelKey: 'supply.pendingOrders.purchaseTypologySemifinished' };
  }
  if (value === 'L') {
    return { labelKey: 'supply.pendingOrders.purchaseTypologyPurchase' };
  }
  return { labelKey: '', fallback: productProdType || value };
}


export interface PendingOrdersSectionProps {
  orders: PendingOrder[];
  setOrders: Dispatch<SetStateAction<PendingOrder[]>>;
  reloadOrders: () => Promise<void>;
  warehouseId: number | null | undefined;
  applyMoqMp: boolean;
  productionWeeks?: Array<{ week: string; iso_week?: string }>;
  /** Precio por tramo de un proveedor para una cantidad; sin él, el servidor recalcula el precio. */
  resolveTierPrice?: (productId: number, supplierId: number, quantity: number) => number | undefined;
  /** Se cambió o duplicó una línea: la vista que tenga filas de esos productos puede refrescarlas. */
  onRowsChanged?: (productIds: number[]) => Promise<void> | void;
  /** La orden se envió al ERP y se marcó como ordenada. */
  onOrderSent?: (order: PendingOrder) => Promise<void> | void;
  /** Se quitaron líneas u órdenes: sus productos vuelven a aparecer como sugeridos. */
  onItemsCancelled?: (productIds: number[]) => void;
  /** Invalida la caché local de la tabla de compras del almacén (por defecto, la caché compartida). */
  onInvalidateTable?: (warehouseId: number) => void;
}

/** Órdenes pendientes del almacén: detalle de líneas, descuentos, fechas de entrega, envío al ERP, Excel y borrado. */
export function PendingOrdersSection({
  orders: pendingOrders,
  setOrders: setPendingOrders,
  reloadOrders: loadPendingOrders,
  warehouseId,
  applyMoqMp,
  productionWeeks = [],
  resolveTierPrice,
  onRowsChanged,
  onOrderSent,
  onItemsCancelled,
  onInvalidateTable,
}: PendingOrdersSectionProps) {
  const { t } = useTranslation();
  const { format: formatMoney } = useCurrency();
  const { selectedWarehouse } = useWarehouse();
  const invalidateTable = onInvalidateTable ?? invalidateSupplyTableCache;
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const pendingQtyStepBaseRef = useRef<Record<number, number>>({});

  // Estados para edición de órdenes pendientes
  const [editingQuantities, setEditingQuantities] = useState<Record<number, boolean>>({});
  const [deletingProducts, setDeletingProducts] = useState<Record<number, boolean>>({});
  const [deletingOrders, setDeletingOrders] = useState<Record<number, boolean>>({});
  const [duplicatingItems, setDuplicatingItems] = useState<Record<number, boolean>>({});

  // Estados para selección múltiple
  const [selectedItems, setSelectedItems] = useState<Record<number, Set<number>>>({}); // orderId -> Set<itemId>
  const [bulkDeleting, setBulkDeleting] = useState<Record<number, boolean>>({}); // orderId -> boolean
  const [sendingOrders, setSendingOrders] = useState<Record<number, boolean>>({});
  const [pendingOrderPages, setPendingOrderPages] = useState<Record<number, number>>({});
  const [pendingOrderItemsPageSize, setPendingOrderItemsPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [orderSubmissionData, setOrderSubmissionData] = useState<Record<number, { dueDate: string; discountDraft?: string }>>({});
  /** Fechas de entrega por línea (id de PendingOrderItem); solo estado local, no persistido en backend */
  const [pendingOrderLineDueDates, setPendingOrderLineDueDates] = useState<Record<number, string>>({});
  const [lineDiscountInputs, setLineDiscountInputs] = useState<Record<number, string>>({});
  const [openLineDiscountEditor, setOpenLineDiscountEditor] = useState<Record<number, boolean>>({});


  useEffect(() => {
    let cancelled = false;
    const syncOrderSubmissionDefaults = () => {
      const todayIso = new Date().toISOString().split('T')[0];
      const defaultsByOrder = pendingOrders.map(order => ({
        orderId: order.id,
        dueDate: order.suggested_due_date || todayIso,
        discountDraft: order.discount === null || order.discount === undefined
          ? ''
          : (Number(order.discount) * 100).toFixed(2),
      }));

      if (cancelled) return;

      setOrderSubmissionData(prev => {
        const next = { ...prev };
        const activeOrderIds = new Set<number>();

        defaultsByOrder.forEach(({ orderId, dueDate, discountDraft }) => {
          activeOrderIds.add(orderId);
          if (!next[orderId]) {
            next[orderId] = { dueDate, discountDraft };
          } else if (next[orderId].discountDraft === undefined) {
            next[orderId] = { ...next[orderId], discountDraft };
          }
        });

        Object.keys(next).forEach(key => {
          const numericId = Number(key);
          if (!activeOrderIds.has(numericId)) {
            delete next[numericId];
          }
        });

        return next;
      });
    };

    syncOrderSubmissionDefaults();
    return () => {
      cancelled = true;
    };
  }, [pendingOrders]);

  const parseDiscountInput = useCallback((raw: string): number | null | 'invalid' => {
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) return 'invalid';
    return parsed / 100;
  }, []);

  const getLineEffectiveDiscount = useCallback((order: PendingOrder, item: PendingOrder['items'][number]): number | null => {
    if (item.discount !== null && item.discount !== undefined) {
      return Number(item.discount);
    }
    if (order.apply_discount === true && order.discount !== null && order.discount !== undefined) {
      return Number(order.discount);
    }
    return null;
  }, []);

  const getLineBaseTotal = useCallback((item: PendingOrder['items'][number]): number => {
    const quantity = Number(item.cantidad) || 0;
    const price = Number(item.precio_estimado_unitario) || 0;
    const peso = item.peso_unitario != null ? Number(item.peso_unitario) : 1;
    return quantity * price * (Number.isFinite(peso) ? peso : 1);
  }, []);

  const getLineDiscountedTotal = useCallback((order: PendingOrder, item: PendingOrder['items'][number]): number => {
    const base = getLineBaseTotal(item);
    const effectiveDiscount = getLineEffectiveDiscount(order, item);
    if (effectiveDiscount === null) return base;
    return base * (1 - effectiveDiscount);
  }, [getLineBaseTotal, getLineEffectiveDiscount]);

  const getOrderVisualTotals = useCallback((order: PendingOrder) => {
    const baseTotal = order.items.reduce((sum, item) => sum + getLineBaseTotal(item), 0);
    const discountedTotal = order.items.reduce((sum, item) => sum + getLineDiscountedTotal(order, item), 0);
    return { baseTotal, discountedTotal };
  }, [getLineBaseTotal, getLineDiscountedTotal]);

  const handleUpdateOrderDiscountConfig = useCallback(async (
    orderId: number,
    payload: { discount?: number | null; apply_discount?: boolean | null }
  ) => {
    setPendingOrders(prev => prev.map(order => (order.id === orderId ? { ...order, ...payload } : order)));
    try {
      await supplyService.updatePendingOrder(orderId, payload);
    } catch (error) {
      console.error('Error updating pending order discount config:', error);
      await loadPendingOrders();
    }
  }, [loadPendingOrders, setPendingOrders]);

  const handleSaveOrderDiscount = useCallback(async (orderId: number, rawValue: string) => {
    const parsed = parseDiscountInput(rawValue);
    if (parsed === 'invalid') {
      alert(t('supply.pendingOrders.discountRangeError'));
      await loadPendingOrders();
      return;
    }
    await handleUpdateOrderDiscountConfig(orderId, { discount: parsed });
  }, [handleUpdateOrderDiscountConfig, loadPendingOrders, parseDiscountInput, t]);

  const handleMarkOrderAsOrdered = useCallback(async (orderId: number) => {
    const order = pendingOrders.find(o => o.id === orderId);
    if (!order) {
      console.warn('Orden no encontrada al intentar enviarla al ERP', orderId);
      return;
    }

    if (!order.proveedor_code) {
      alert('La orden no tiene código de proveedor asignado.');
      return;
    }

    if (!order.items || order.items.length === 0) {
      alert('La orden no tiene productos para enviar.');
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    const submission = orderSubmissionData[orderId] ?? {
      dueDate: today
    };
    const orderDate = order.fecha ? order.fecha.split('T')[0] : today;
    const organizationId = Number(selectedWarehouse?.organization ?? 6);
    const commentsToSend = 'StockerAI solutions';

    const headerDue = submission.dueDate || today;
    const warehouseCode = selectedWarehouse?.code?.trim() || '01';
    const payload: PurchaseOrderPayload = {
      organization_id: organizationId,
      supplier_code: order.proveedor_code,
      date: orderDate,
      due_date: headerDue,
      comments: commentsToSend,
      warehouse_id: warehouseId ?? undefined,
      // Se usa para trazabilidad en logs internos del proxy (no se envía al ERP externo).
      pending_order_id: order.id,
      document_lines: order.items.map(item => {
        const line: PurchaseOrderLinePayload = {
          cod_prod: item.producto_code,
          quantity: Number(item.cantidad) || 0,
          warehouse_id: warehouseCode,
          unit_price: Number(item.precio_estimado_unitario) || 0
        };
        const effectiveDiscount = getLineEffectiveDiscount(order, item);
        if (effectiveDiscount !== null) {
          line.discount = effectiveDiscount;
        }
        const lineDue = pendingOrderLineDueDates[item.id];
        if (lineDue && lineDue !== headerDue) {
          line.due_date = lineDue;
        }
        return line;
      })
    };
    if (organizationId >= 10) {
      payload.numero_orden = order.numero_orden;
      payload.order_number = order.numero_orden;
    }
    console.log('[Supply][ERP] Payload enviado:', payload);
    setSendingOrders(prev => ({ ...prev, [orderId]: true }));

    try {
      const erpResponse = await supplyService.createPurchaseOrderInErp(payload);
      if (erpResponse && erpResponse.success === false) {
        throw new Error(typeof erpResponse.message === 'string' ? erpResponse.message : 'El ERP rechazó el pedido.');
      }
      const response = await supplyService.markPendingOrderAsOrdered(orderId);

      if (response.success && isMountedRef.current) {
        // Optimistic UI: ocultar la orden marcada como enviada inmediatamente.
        setPendingOrders(prev => prev.filter(o => o.id !== orderId));
        if (typeof warehouseId === 'number') {
          invalidateTable(warehouseId);
        }
        await loadPendingOrders();
        await onOrderSent?.(order);
      } else if (!response.success) {
        alert(response.message || 'No se pudo marcar la orden como enviada.');
      }
    } catch (error) {
      console.error('Error al enviar pedido al ERP o marcar como ordenado:', error);
      const apiError = error as { message?: string; detail?: string };
      const message =
        apiError?.detail ||
        apiError?.message ||
        (error instanceof Error ? error.message : null) ||
        'No se pudo enviar el pedido al ERP. Por favor, revisa los datos e inténtalo nuevamente.';
      alert(message);
    } finally {
      setSendingOrders(prev => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });
    }
  }, [pendingOrders, orderSubmissionData, pendingOrderLineDueDates, selectedWarehouse, loadPendingOrders, onOrderSent, invalidateTable, warehouseId, getLineEffectiveDiscount, setPendingOrders]);

  // Función para actualizar cantidad y/o precio de un producto en una orden pendiente
  const handleUpdateProductQuantity = useCallback(async (
    orderId: number,
    itemId: number,
    newQuantity: number,
    newPrice?: number,
    newDiscount?: number | null
  ) => {
    if (!isMountedRef.current) return;

    setEditingQuantities(prev => ({ ...prev, [itemId]: true }));

    try {
      // Extraer productId y supplierId ANTES de actualizar el estado
      const order = pendingOrders.find(o => o.id === orderId);
      const item = order?.items.find(i => i.id === itemId);

      if (!order || !item) {
        console.error('Order or item not found');
        return;
      }

      const productIdForUserQuantityUpdate = item.producto;
      const supplierIdForUserQuantityUpdate = order.proveedor;

      // Si solo cambia cantidad, resolver tramo como en Orden sugerido (SUP-ORD-008)
      let resolvedUnitPrice =
        newPrice !== undefined
          ? Number(newPrice)
          : Number(item.precio_estimado_unitario) || 0;
      if (newPrice === undefined) {
        const tierPrice = resolveTierPrice?.(item.producto, order.proveedor, Number(newQuantity) || 0);
        if (tierPrice !== undefined) resolvedUnitPrice = tierPrice;
      }

      // Actualizar estado local inmediatamente para feedback visual
      setPendingOrders(prev => prev.map(order => {
        if (order.id === orderId) {
          const updatedItems = order.items.map(item => {
            if (item.id === itemId) {
              const quantity = Number(newQuantity) || 0;
              const price = resolvedUnitPrice;
              const peso = Number(item.peso_unitario) || 1;
              const updatedTotalValue = quantity * peso * price;
              const updatedItem = {
                ...item,
                cantidad: newQuantity,
                precio_estimado_unitario: price,
                total_value: updatedTotalValue
              };

              if (newDiscount !== undefined) {
                updatedItem.discount = newDiscount;
              }

              return updatedItem;
            }
            return item;
          });

          // Recalcular total de la orden
          const newOrderTotal = updatedItems.reduce((sum, item) => {
            const itemTotal = Number(item.total_value) || 0;
            return sum + itemTotal;
          }, 0);

          return {
            ...order,
            items: updatedItems,
            total_value: newOrderTotal
          };
        }
        return order;
      }));

      // Actualizar pending order item (BE recalcula tramo si no mandamos precio)
      const response = await supplyService.updatePendingOrderItemQuantity(orderId, itemId, newQuantity, newPrice, newDiscount);
      if (!response.success) {
        // Si falla la actualización, revertir cambios locales
        await loadPendingOrders();
        return;
      }

      // Sincronizar con respuesta del backend (precio por tramo / totales oficiales)
      if (response.updated_item) {
        setPendingOrders(prev => prev.map(o => {
          if (o.id !== orderId) return o;
          const updatedItems = o.items.map(i =>
            i.id === itemId ? { ...i, ...response.updated_item! } : i,
          );
          return {
            ...o,
            items: updatedItems,
            total_value:
              response.new_order_total !== undefined
                ? response.new_order_total
                : o.total_value,
          };
        }));
      }

      // Actualizar user quantity DESPUÉS de que el pending order se actualizó exitosamente
      if (
        productIdForUserQuantityUpdate !== undefined &&
        productIdForUserQuantityUpdate !== null &&
        warehouseId &&
        typeof warehouseId === 'number'
      ) {
        await supplyService.updateUserQuantity(
          productIdForUserQuantityUpdate,
          newQuantity,
          warehouseId,
          supplierIdForUserQuantityUpdate
        );
        invalidateTable(warehouseId);
        // Refresh dirigido solo por el producto editado en el pedido, en
        // lugar de recargar la tabla entera.
        await onRowsChanged?.([productIdForUserQuantityUpdate]);
      }

    } catch (error) {
      console.error('Error updating product quantity/price:', error);
      // Revertir cambios en caso de error
      await loadPendingOrders();
    } finally {
      if (isMountedRef.current) {
        setEditingQuantities(prev => ({ ...prev, [itemId]: false }));
      }
    }
  }, [loadPendingOrders, onRowsChanged, resolveTierPrice, invalidateTable, warehouseId, pendingOrders, setPendingOrders]);

  const parseCommittedPendingQuantity = useCallback(
    (rawValue: string, moq?: number) => {
      const parsed = parseSupplyQuantity(rawValue);
      if (parsed === null || parsed <= 0) {
        return null;
      }
      return applyMoqFloorOnCommit(parsed, moq, applyMoqMp);
    },
    [applyMoqMp],
  );

  // Función para duplicar un item de orden pendiente
  const handleDuplicateItem = useCallback(async (orderId: number, itemId: number) => {
    if (!isMountedRef.current) return;

    setDuplicatingItems(prev => ({ ...prev, [itemId]: true }));

    try {
      // Llamar API para duplicar el item
      const response = await supplyService.duplicatePendingOrderItem(orderId, itemId);

      if (response.success && response.duplicated_item) {
        if (typeof warehouseId === 'number') {
          invalidateTable(warehouseId);
        }
        // Actualizar estado local agregando el nuevo item
        setPendingOrders(prev => prev.map(order => {
          if (order.id === orderId) {
            const updatedItems = [...order.items, response.duplicated_item!];
            return {
              ...order,
              items: updatedItems,
              items_count: updatedItems.length,
              total_value: response.new_order_total || order.total_value
            };
          }
          return order;
        }));
        if (typeof warehouseId === 'number') {
          // Refresh dirigido solo por el producto duplicado.
          await onRowsChanged?.([response.duplicated_item.producto]);
        }
      } else {
        // Si falla la duplicación, recargar órdenes
        await loadPendingOrders();
      }

    } catch (error) {
      console.error('Error duplicating item:', error);
      // Revertir cambios en caso de error
      await loadPendingOrders();
    } finally {
      if (isMountedRef.current) {
        setDuplicatingItems(prev => ({ ...prev, [itemId]: false }));
      }
    }
  }, [loadPendingOrders, onRowsChanged, invalidateTable, warehouseId, setPendingOrders]);

  // Función para exportar orden pendiente a Excel
  const handleExportOrderToExcel = useCallback((order: PendingOrder) => {
    try {
      // Crear datos para exportar
      const exportData = order.items.map(item => ({
        'Proveedor': order.proveedor_name,
        'Codigo Proveedor': order.proveedor_code,
        'Codigo Producto': item.producto_code, // Sin tilde para evitar problemas de codificación
        'Codigo Producto Proveedor': item.producto_client_code ?? '',
        'Nombre Producto': item.producto_name,
        'Cantidad': item.cantidad,
        'Precio Unitario': Number(item.precio_estimado_unitario).toFixed(2), // Sin símbolo €
        'Total': Number(item.total_value).toFixed(2) // Sin símbolo €
      }));

      // Crear un libro de trabajo
      const wb = XLSX.utils.book_new();

      // Crear una hoja de cálculo con los datos
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Ajustar el ancho de las columnas
      const columnWidths = [
        { wch: 25 }, // Proveedor
        { wch: 18 }, // Codigo Proveedor
        { wch: 18 }, // Codigo Producto
        { wch: 24 }, // Codigo Producto Proveedor
        { wch: 35 }, // Nombre Producto
        { wch: 12 }, // Cantidad
        { wch: 15 }, // Precio Unitario
        { wch: 15 }, // Total
      ];
      ws['!cols'] = columnWidths;

      // Agregar la hoja al libro
      XLSX.utils.book_append_sheet(wb, ws, 'Orden');

      // Generar el nombre del archivo (sanitizado)
      const sanitizedName = order.proveedor_name.replace(/[^a-z0-9]/gi, '_');
      const fileName = `orden_${order.numero_orden}_${sanitizedName}.xlsx`;

      // Descargar el archivo
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Error exporting order to Excel:', error);
    }
  }, []);

  // Función para eliminar un producto de una orden pendiente
  const handleDeleteProductFromOrder = useCallback(async (orderId: number, itemId: number) => {
    if (!isMountedRef.current) return;

    setDeletingProducts(prev => ({ ...prev, [itemId]: true }));

    try {
      // Obtener el item que se va a eliminar para determinar si es el principal
      const order = pendingOrders.find(o => o.id === orderId);

      if (!order) return;

      const itemToDelete = order.items.find(i => i.id === itemId);

      if (!itemToDelete) return;
      const productIdToReappear = itemToDelete.producto;

      // Obtener todos los items del mismo producto, ordenados por fecha de creación
      const sameProductItems = order.items
        .filter(i => i.producto === itemToDelete.producto)
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      const isMainItem = sameProductItems[0]?.id === itemId;

      // Actualizar estado local inmediatamente
      // Si es el item principal, eliminar todos los items del mismo producto
      // Si es un item de descuento, solo eliminar ese item
      setPendingOrders(prev => prev.map(order => {
        if (order.id === orderId) {
          let updatedItems;
          if (isMainItem && sameProductItems.length > 1) {
            // Eliminar todos los items del mismo producto
            const productIdsToRemove = new Set(sameProductItems.map(i => i.id));
            updatedItems = order.items.filter(item => !productIdsToRemove.has(item.id));
          } else {
            // Solo eliminar este item
            updatedItems = order.items.filter(item => item.id !== itemId);
          }

          const newOrderTotal = updatedItems.reduce((sum, item) => {
            const itemTotal = Number(item.total_value) || 0;
            return sum + itemTotal;
          }, 0);

          return {
            ...order,
            items: updatedItems,
            items_count: updatedItems.length,
            total_value: newOrderTotal
          };
        }
        return order;
      }));

      // Llamar API para eliminar del backend
      const response = await supplyService.deletePendingOrderItem(orderId, itemId);

      if (!response.success) {
        // Si falla la eliminación, revertir cambios locales
        await loadPendingOrders();
      } else {
        setPendingOrderLineDueDates(prev => {
          const next = { ...prev };
          const idsToClear =
            isMainItem && sameProductItems.length > 1
              ? sameProductItems.map(i => i.id)
              : [itemId];
          idsToClear.forEach(id => {
            delete next[id];
          });
          return next;
        });
        onItemsCancelled?.([productIdToReappear]);
      }

    } catch (error) {
      console.error('Error deleting product from order:', error);
      // Revertir cambios en caso de error
      await loadPendingOrders();
    } finally {
      if (isMountedRef.current) {
        setDeletingProducts(prev => ({ ...prev, [itemId]: false }));
      }
    }
  }, [loadPendingOrders, pendingOrders, onItemsCancelled, setPendingOrders]);

  // Función para eliminar una orden completa
  const handleDeleteOrder = useCallback(async (orderId: number) => {
    if (!isMountedRef.current) return;

    setDeletingOrders(prev => ({ ...prev, [orderId]: true }));

    try {
      const orderToDelete = pendingOrders.find(order => order.id === orderId);
      const productIdsToReappear = orderToDelete
        ? Array.from(new Set(orderToDelete.items.map((item) => item.producto)))
        : [];
      // Actualizar estado local inmediatamente
      setPendingOrders(prev => prev.filter(order => order.id !== orderId));

      // Llamar API para eliminar orden del backend
      const response = await supplyService.deletePendingOrder(orderId);

      if (!response.success) {
        // Si falla la eliminación, revertir cambios locales
        await loadPendingOrders();
      } else {
        onItemsCancelled?.(productIdsToReappear);
      }

    } catch (error) {
      console.error('Error deleting order:', error);
      // Revertir cambios en caso de error
      await loadPendingOrders();
    } finally {
      if (isMountedRef.current) {
        setDeletingOrders(prev => ({ ...prev, [orderId]: false }));
      }
    }
  }, [loadPendingOrders, pendingOrders, onItemsCancelled, setPendingOrders]);

  // Funciones para selección múltiple
  const toggleItemSelection = useCallback((orderId: number, itemId: number) => {
    setSelectedItems(prev => {
      const orderSelectedItems = prev[orderId] || new Set();
      const newOrderSelectedItems = new Set(orderSelectedItems);

      if (newOrderSelectedItems.has(itemId)) {
        newOrderSelectedItems.delete(itemId);
      } else {
        newOrderSelectedItems.add(itemId);
      }

      return {
        ...prev,
        [orderId]: newOrderSelectedItems
      };
    });
  }, []);

  const selectAllItemsInOrder = useCallback((orderId: number, allItemIds: number[]) => {
    setSelectedItems(prev => ({
      ...prev,
      [orderId]: new Set(allItemIds)
    }));
  }, []);

  const clearSelectionInOrder = useCallback((orderId: number) => {
    setSelectedItems(prev => {
      const newSelectedItems = { ...prev };
      delete newSelectedItems[orderId];
      return newSelectedItems;
    });
  }, []);

  const getSelectedItemsInOrder = useCallback((orderId: number) => {
    return selectedItems[orderId] || new Set();
  }, [selectedItems]);

  // Función para eliminar múltiples items
  const handleBulkDeleteItems = useCallback(async (orderId: number) => {
    const selectedItemIds = getSelectedItemsInOrder(orderId);

    if (selectedItemIds.size === 0) {
      return;
    }

    if (!isMountedRef.current) return;

    setBulkDeleting(prev => ({ ...prev, [orderId]: true }));

    // Capturar product_ids afectados ANTES de filtrar los items del pedido;
    // luego los usamos para el refresh dirigido.
    const orderBeforeDelete = pendingOrders.find(o => o.id === orderId);
    const affectedProductIds = orderBeforeDelete
      ? orderBeforeDelete.items
          .filter((item) => selectedItemIds.has(item.id))
          .map((item) => item.producto)
      : [];

    try {
      // Actualizar estado local inmediatamente
      setPendingOrders(prev => prev.map(order => {
        if (order.id === orderId) {
          const updatedItems = order.items.filter(item => !selectedItemIds.has(item.id));
          const newOrderTotal = updatedItems.reduce((sum, item) => {
            const itemTotal = Number(item.total_value) || 0;
            return sum + itemTotal;
          }, 0);

          return {
            ...order,
            items: updatedItems,
            items_count: updatedItems.length,
            total_value: newOrderTotal
          };
        }
        return order;
      }));

      // Eliminar items del backend uno por uno
      const deletePromises = Array.from(selectedItemIds).map(itemId =>
        supplyService.deletePendingOrderItem(orderId, itemId)
      );

      const results = await Promise.allSettled(deletePromises);

      // Verificar si alguna eliminación falló
      const failedDeletes = results.filter(result =>
        result.status === 'rejected' ||
        (result.status === 'fulfilled' && !result.value.success)
      );

      if (failedDeletes.length > 0) {
        await loadPendingOrders();
      } else {
        // Limpiar selección después de eliminación exitosa
        clearSelectionInOrder(orderId);

        onItemsCancelled?.(affectedProductIds);
      }

    } catch {
      // Revertir cambios en caso de error
      await loadPendingOrders();
    } finally {
      if (isMountedRef.current) {
        setBulkDeleting(prev => ({ ...prev, [orderId]: false }));
      }
    }
  }, [getSelectedItemsInOrder, clearSelectionInOrder, loadPendingOrders, onItemsCancelled, pendingOrders, setPendingOrders]);

  return (
  <div className="bg-white rounded-lg shadow-sm p-6">
    <div className="flex justify-between items-center mb-4">
      <h2 className="text-lg font-medium text-gray-900">
        {t('supply.pendingOrders.title')}
      </h2>
      <div className="text-sm text-gray-500">
        {pendingOrders.length} {t('supply.pendingOrders.pendingCount')}
      </div>
    </div>

    {pendingOrders.length > 0 ? (
      <div className="space-y-6">
        {pendingOrders.map((order) => {
          const submission = orderSubmissionData[order.id];
          const dueDateValue = submission?.dueDate ?? new Date().toISOString().split('T')[0];
          const orderDiscountInputValue = submission?.discountDraft ?? (
            order.discount === null || order.discount === undefined
              ? ''
              : (Number(order.discount) * 100).toFixed(2)
          );
          const orderTotals = getOrderVisualTotals(order);
          const showDiscountedOrderTotal = Math.abs(orderTotals.baseTotal - orderTotals.discountedTotal) >= 0.005;
          return (
            <div key={order.id} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
              {/* Header de la orden con botón de eliminar */}
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {order.numero_orden}
                    </h3>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {(() => {
                        const uniqueProducts = new Set(order.items.map(item => item.producto_code));
                        return t('supply.pendingOrders.nProducts', { count: uniqueProducts.size });
                      })()}
                    </span>
                    {(() => {
                      const typology = getPendingOrderPurchaseTypology(order.product_prod_type);
                      if (!typology) {
                        return null;
                      }
                      return (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          {t('supply.pendingOrders.purchaseTypologyLabel')}:{' '}
                          {typology.fallback || t(typology.labelKey)}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">{t('supply.pendingOrders.supplierLabel')}:</span>
                      <span className="ml-1">{order.proveedor_name}</span>
                    </div>
                    <div>
                      <span className="font-medium">{t('supply.pendingOrders.date')}:</span>
                      <span className="ml-1">{new Date(order.fecha).toLocaleDateString()}</span>
                    </div>
                    <div>
                      <span className="font-medium">{t('supply.pendingOrders.totalLabel')}:</span>
                      <span className="ml-1 font-semibold text-green-600">
                        {formatMoney(showDiscountedOrderTotal ? orderTotals.discountedTotal : orderTotals.baseTotal)}
                      </span>
                      {showDiscountedOrderTotal ? (
                        <span className="ml-2 text-xs text-gray-500 line-through">{formatMoney(orderTotals.baseTotal)}</span>
                      ) : null}
                    </div>
                    {order.production_week ? (
                      <div>
                        <span className="font-medium">{t('supply.pendingOrders.productionWeekLabel')}:</span>
                        <span className="ml-1">
                          {productionWeeks.find((week) => week.week === order.production_week)?.iso_week
                            || order.production_week}
                          {order.so_external_id ? ` · ${order.so_external_id}` : ''}
                        </span>
                      </div>
                    ) : null}
                  </div>
                  <div className="mt-4 grid gap-3 md:grid-cols-2 max-w-4xl items-start">
                    <div className="flex flex-col text-sm text-gray-600">
                      <span className="font-medium text-gray-700">{t('supply.pendingOrders.deliveryDateLabel')}</span>
                      <div className="mt-1 min-h-[1.25rem] text-xs text-gray-500">
                        {order.lead_time_days_assigned !== undefined &&
                          order.lead_time_days_assigned !== null &&
                          order.lead_time_days_assigned > 0
                          ? t('supply.pendingOrders.leadTimeProviderAssigned', {
                              count: order.lead_time_days_assigned
                            })
                          : null}
                        {(order.suggested_due_date_source === 'need_date' ||
                          order.suggested_due_date_source === 'urgent_lead_time') &&
                        order.lead_time_due_date
                          ? (() => {
                              const formatIso = (iso: string) => {
                                const [y, m, d] = iso.split('-').map(Number);
                                return new Date(y, m - 1, d).toLocaleDateString(undefined, {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                });
                              };
                              return (
                                <span className="block font-medium text-amber-700">
                                  {t('supply.pendingOrders.dueDateMovedToNeed', {
                                    count: order.need_date_items_count ?? 0,
                                    date: formatIso(order.lead_time_due_date)
                                  })}
                                  {order.suggested_due_date_source === 'urgent_lead_time' &&
                                  order.earliest_need_date ? (
                                    <span className="block font-normal">
                                      {t('supply.pendingOrders.dueDateUrgentLeadTimeFloor', {
                                        need: formatIso(order.earliest_need_date),
                                        pct: order.urgent_lead_time_pct ?? 50
                                      })}
                                    </span>
                                  ) : null}
                                </span>
                              );
                            })()
                          : null}
                      </div>
                      <input
                        type="date"
                        value={dueDateValue}
                        onChange={(event) => {
                          const newValue = event.target.value;
                          setOrderSubmissionData(prev => {
                            const next = { ...prev };
                            const current = next[order.id] ?? { dueDate: newValue };
                            next[order.id] = { ...current, dueDate: newValue };
                            return next;
                          });
                        }}
                        className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                      />
                    </div>
                    <div className="flex flex-col text-sm text-gray-600">
                      <span className="font-medium text-gray-700">{t('supply.pendingOrders.orderDiscountLabel')}</span>
                      <div className="mt-1 min-h-[1.25rem]" />
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          value={orderDiscountInputValue}
                          placeholder={t('supply.pendingOrders.discountPlaceholder')}
                          onChange={(event) => {
                            const newValue = event.target.value;
                            setOrderSubmissionData(prev => {
                              const next = { ...prev };
                              const current = next[order.id] ?? { dueDate: dueDateValue };
                              next[order.id] = { ...current, discountDraft: newValue };
                              return next;
                            });
                          }}
                          onBlur={() => {
                            void handleSaveOrderDiscount(order.id, orderDiscountInputValue);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              void handleSaveOrderDiscount(order.id, orderDiscountInputValue);
                            }
                          }}
                          className="w-24 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                        />
                        <span className="text-sm font-medium text-gray-600">%</span>
                        <label className="inline-flex items-center gap-1 text-xs text-gray-700 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={order.apply_discount === true}
                            onChange={(event) => {
                              void handleUpdateOrderDiscountConfig(order.id, {
                                apply_discount: event.target.checked
                              });
                            }}
                            className="h-4 w-4 rounded border-gray-300 text-[#1681ba] focus:ring-[#1681ba]"
                          />
                          <span>{t('supply.pendingOrders.applyOrderDiscountLabel')}</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Botones de acción para la orden completa */}
                <div className="flex items-center space-x-2 ml-4">
                  {/* Botón de exportar a Excel */}
                  <button
                    onClick={() => handleExportOrderToExcel(order)}
                    className="px-3 py-2 text-sm font-medium bg-blue-100 text-blue-700 border border-blue-300 rounded-md hover:bg-blue-200 transition-colors flex items-center space-x-1"
                    title={t('supply.pendingOrders.exportExcelTitle')}
                  >
                    <Download className="h-4 w-4" />
                    <span>{t('supply.pendingOrders.excel')}</span>
                  </button>

                  {/* Botones de selección múltiple */}
                  {getSelectedItemsInOrder(order.id).size > 0 && (
                    <>
                      <button
                        onClick={() => handleBulkDeleteItems(order.id)}
                        disabled={bulkDeleting[order.id]}
                        className="px-3 py-2 text-sm font-medium bg-orange-100 text-orange-700 border border-orange-300 rounded-md hover:bg-orange-200 transition-colors disabled:opacity-50 flex items-center space-x-1"
                      >
                        {bulkDeleting[order.id] ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-700"></div>
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                        <span>
                          {t('supply.pendingOrders.deleteSelectedCount', {
                            count: getSelectedItemsInOrder(order.id).size,
                          })}
                        </span>
                      </button>

                      <button
                        onClick={() => clearSelectionInOrder(order.id)}
                        className="px-3 py-2 text-sm font-medium bg-gray-100 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors flex items-center space-x-1"
                      >
                        <X className="h-4 w-4" />
                        <span>{t('supply.pendingOrders.cancelSelection')}</span>
                      </button>
                    </>
                  )}

                  <button
                    onClick={() => handleMarkOrderAsOrdered(order.id)}
                    disabled={!!sendingOrders[order.id]}
                    className="px-3 py-2 text-sm font-medium bg-green-100 text-green-700 border border-green-300 rounded-md hover:bg-green-200 transition-colors flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {sendingOrders[order.id] ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-700"></div>
                    ) : (
                      <CheckCircle className="h-4 w-4" />
                    )}
                    <span>
                      {sendingOrders[order.id]
                        ? t('supply.pendingOrders.sending')
                        : t('supply.messages.markAsSent')}
                    </span>
                  </button>

                  <button
                    onClick={() => handleDeleteOrder(order.id)}
                    disabled={deletingOrders[order.id]}
                    className="px-3 py-2 text-sm font-medium bg-red-100 text-red-700 border border-red-300 rounded-md hover:bg-red-200 transition-colors disabled:opacity-50 flex items-center space-x-1"
                  >
                    {deletingOrders[order.id] ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-700"></div>
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                    <span>{t('supply.pendingOrders.deleteOrder')}</span>
                  </button>
                </div>
              </div>

              {/* Tabla de productos con funcionalidad de edición */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 bg-white rounded-lg">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={order.items.length > 0 && getSelectedItemsInOrder(order.id).size === order.items.length}
                            onChange={() => {
                              if (getSelectedItemsInOrder(order.id).size === order.items.length) {
                                clearSelectionInOrder(order.id);
                              } else {
                                selectAllItemsInOrder(order.id, order.items.map(item => item.id));
                              }
                            }}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span>{t('supply.pendingOrders.productLabel')}</span>
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('supply.table.quantity')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('supply.pendingOrders.unitPriceLabel')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('supply.pendingOrders.discountLabel')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('supply.pendingOrders.discountedUnitPriceLabel')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('supply.pendingOrders.totalLabel')}
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('supply.pendingOrders.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(() => {
                      // Agrupar items por producto para mostrar conexión visual
                      const itemsByProduct = order.items.reduce((acc, item) => {
                        if (!acc[item.producto]) {
                          acc[item.producto] = [];
                        }
                        acc[item.producto].push(item);
                        return acc;
                      }, {} as Record<number, typeof order.items>);

                      // Ordenar items dentro de cada grupo por fecha de creación (más antiguo primero)
                      Object.keys(itemsByProduct).forEach(productId => {
                        itemsByProduct[parseInt(productId)].sort((a, b) => {
                          const dateA = new Date(a.created_at).getTime();
                          const dateB = new Date(b.created_at).getTime();
                          return dateA - dateB; // Orden ascendente (más antiguo primero)
                        });
                      });

                      // Obtener items ordenados por producto (agrupados)
                      const groupedItems: typeof order.items = [];
                      Object.values(itemsByProduct).forEach(productItems => {
                        groupedItems.push(...productItems);
                      });

                      const pageSize = pendingOrderItemsPageSize;
                      const totalItems = groupedItems.length;
                      const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
                      const currentPage = pendingOrderPages[order.id] ?? 1;
                      const safePage = Math.min(Math.max(currentPage, 1), totalPages);
                      const pageStart = (safePage - 1) * pageSize;
                      const pageEnd = pageStart + pageSize;
                      const pagedItems = groupedItems.slice(pageStart, pageEnd);

                      return pagedItems.map((item, localIndex) => {
                        const globalIndex = pageStart + localIndex;
                        const productItems = itemsByProduct[item.producto];
                        const isMultipleItems = productItems.length > 1;
                        const itemIndexInGroup = productItems.findIndex(i => i.id === item.id);
                        const isFirstInGroup = itemIndexInGroup === 0;

                        // Diseño con fondo compartido para agrupar items del mismo producto
                        const isLastInGroup = itemIndexInGroup === productItems.length - 1;
                        const isGroupStart = isFirstInGroup && isMultipleItems;
                        const isGroupEnd = isLastInGroup && isMultipleItems;

                        return (
                          <tr key={item.id} className={`
                            ${isMultipleItems ? 'bg-blue-50/30' : globalIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                            hover:bg-blue-50/50
                            ${isGroupStart ? 'border-t-2 border-blue-400 rounded-t-lg' : ''}
                            ${isGroupEnd ? 'border-b-2 border-blue-400 rounded-b-lg mb-2' : ''}
                            ${isMultipleItems && !isGroupStart && !isGroupEnd ? 'border-l-2 border-r-2 border-blue-200' : ''}
                          `}>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center space-x-3">
                                <input
                                  type="checkbox"
                                  checked={getSelectedItemsInOrder(order.id).has(item.id)}
                                  onChange={() => toggleItemSelection(order.id, item.id)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <div className="flex items-start flex-1">
                                  <div className="flex-1">
                                    {isFirstInGroup ? (
                                      // Primera fila: mostrar información completa del producto
                                      <div className="flex items-center space-x-3">
                                        <div className="flex-shrink-0">
                                          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                            <Package className="h-4 w-4 text-blue-600" />
                                          </div>
                                        </div>
                                        <div className="flex-1">
                                          <div className="flex items-center space-x-2">
                                            <div className="text-sm font-semibold text-gray-900">{item.producto_code}</div>
                                            <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full border border-green-200">
                                              {t('supply.pendingOrders.linePrincipal')}
                                            </span>
                                          </div>
                                          <div className="text-sm text-gray-600 mt-0.5">{item.producto_name}</div>
                                        </div>
                                      </div>
                                    ) : (
                                      // Filas siguientes: mostrar como variante de descuento
                                      <div className="flex items-center space-x-3 pl-8">
                                        <div className="flex-shrink-0">
                                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center shadow-sm border border-orange-200">
                                            <span className="text-orange-600 font-bold text-sm">%</span>
                                          </div>
                                        </div>
                                        <div className="flex-1">
                                          <div className="flex items-center space-x-2">
                                            <div className="text-sm font-medium text-gray-700">{item.producto_code}</div>
                                            <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-full border border-orange-200">
                                              {t('supply.pendingOrders.lineDiscount')}
                                            </span>
                                          </div>
                                          <div className="text-sm text-gray-500 mt-0.5">{item.producto_name}</div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                <input
                                  type="number"
                                  min={getSupplyQuantityInputMin(item.moq, applyMoqMp, item.mp)}
                                  step="any"
                                  defaultValue={item.cantidad}
                                  onFocus={(e) => {
                                    pendingQtyStepBaseRef.current[item.id] =
                                      parseSupplyQuantity(e.target.value)
                                      ?? Number(item.cantidad);
                                  }}
                                  onChange={(e) => {
                                    const prev =
                                      pendingQtyStepBaseRef.current[item.id]
                                      ?? Number(item.cantidad);
                                    const resolved =
                                      resolveSupplyQuantityAfterStepInteraction(
                                        prev,
                                        e.target.value,
                                        item.mp,
                                        item.moq,
                                        applyMoqMp,
                                      );
                                    if (resolved === null) {
                                      return;
                                    }
                                    const parsed = parseSupplyQuantity(e.target.value);
                                    if (
                                      parsed !== null
                                      && !quantitiesEqual(parsed, resolved)
                                    ) {
                                      e.target.value = String(resolved);
                                    }
                                    pendingQtyStepBaseRef.current[item.id] = resolved;
                                  }}
                                  onBlur={(e) => {
                                    const newQuantity = parseCommittedPendingQuantity(
                                      e.target.value,
                                      item.moq,
                                    );
                                    if (
                                      newQuantity !== null
                                      && !quantitiesEqual(newQuantity, Number(item.cantidad))
                                    ) {
                                      e.target.value = String(newQuantity);
                                      handleUpdateProductQuantity(order.id, item.id, newQuantity, undefined);
                                    } else if (newQuantity === null) {
                                      e.target.value = item.cantidad.toString();
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                      e.preventDefault();
                                      const el = e.currentTarget;
                                      const current =
                                        parseSupplyQuantity(el.value)
                                        ?? Number(item.cantidad);
                                      const dir = e.key === 'ArrowUp' ? 1 : -1;
                                      const next = stepSupplyQuantityByMp(
                                        current,
                                        dir,
                                        item.mp,
                                        item.moq,
                                        applyMoqMp,
                                      );
                                      el.value = String(next);
                                      pendingQtyStepBaseRef.current[item.id] = next;
                                      return;
                                    }
                                    if (e.key === 'Enter') {
                                      const newQuantity = parseCommittedPendingQuantity(
                                        e.currentTarget.value,
                                        item.moq,
                                      );
                                      if (
                                        newQuantity !== null
                                        && !quantitiesEqual(newQuantity, Number(item.cantidad))
                                      ) {
                                        e.currentTarget.value = String(newQuantity);
                                        pendingQtyStepBaseRef.current[item.id] = newQuantity;
                                        handleUpdateProductQuantity(order.id, item.id, newQuantity, undefined);
                                      } else if (newQuantity === null) {
                                        e.currentTarget.value = item.cantidad.toString();
                                      }
                                    }
                                  }}
                                  className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  disabled={editingQuantities[item.id]}
                                />
                                {editingQuantities[item.id] && (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center space-x-2">
                                <input
                                  key={`pending-price-${item.id}-${Number(item.precio_estimado_unitario)}`}
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  defaultValue={item.precio_estimado_unitario}
                                  onBlur={(e) => {
                                    const rawValue = e.target.value;
                                    const newPrice = parseFloat(rawValue);
                                    if (!isNaN(newPrice) && newPrice !== Number(item.precio_estimado_unitario) && newPrice >= 0) {
                                      handleUpdateProductQuantity(order.id, item.id, item.cantidad, newPrice);
                                    } else if (isNaN(newPrice)) {
                                      e.target.value = item.precio_estimado_unitario.toString(); // Restaurar valor original
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                      const rawValue = e.currentTarget.value;
                                      const newPrice = parseFloat(rawValue);
                                      if (!isNaN(newPrice) && newPrice !== Number(item.precio_estimado_unitario) && newPrice >= 0) {
                                        handleUpdateProductQuantity(order.id, item.id, item.cantidad, newPrice);
                                      } else if (isNaN(newPrice)) {
                                        e.currentTarget.value = item.precio_estimado_unitario.toString(); // Restaurar valor original
                                      }
                                    }
                                  }}
                                  className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  disabled={editingQuantities[item.id]}
                                />
                                <span className="text-sm text-gray-500">
                                  {(() => {
                                    const pesoUnitario = item.peso_unitario != null ? Number(item.peso_unitario) : null;
                                    if (pesoUnitario != null && pesoUnitario > 1) {
                                      return t('supply.pendingOrders.perKg');
                                    }
                                    return `/${resolveSupplyUnitDisplay(item).name}`;
                                  })()}
                                </span>
                                {(() => {
                                  const pesoUnitario = Number(item.peso_unitario) || 1;
                                  if (pesoUnitario > 1) {
                                    return (
                                      <div className="relative group">
                                        <Info className="h-4 w-4 text-gray-400 hover:text-blue-600 cursor-help transition-colors" />
                                        <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                          <p className="leading-relaxed">
                                            {t('supply.pendingOrders.averageWeightTooltip', { weight: pesoUnitario.toFixed(1) })}
                                          </p>
                                          <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-900"></div>
                                        </div>
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                              {openLineDiscountEditor[item.id] ? (
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  value={lineDiscountInputs[item.id] ?? ''}
                                  placeholder={t('supply.pendingOrders.discountPlaceholder')}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    setLineDiscountInputs(prev => ({ ...prev, [item.id]: value }));
                                  }}
                                  onBlur={() => {
                                    const rawValue = lineDiscountInputs[item.id] ?? '';
                                    const parsed = parseDiscountInput(rawValue);
                                    if (parsed === 'invalid') {
                                      alert(t('supply.pendingOrders.discountRangeError'));
                                      setLineDiscountInputs(prev => ({
                                        ...prev,
                                        [item.id]: item.discount === null || item.discount === undefined
                                          ? ''
                                          : (Number(item.discount) * 100).toFixed(2)
                                      }));
                                      return;
                                    }
                                    setOpenLineDiscountEditor(prev => ({ ...prev, [item.id]: false }));
                                    if ((item.discount ?? null) !== parsed) {
                                      void handleUpdateProductQuantity(order.id, item.id, item.cantidad, undefined, parsed);
                                    }
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                      event.preventDefault();
                                      (event.currentTarget as HTMLInputElement).blur();
                                    }
                                  }}
                                  className="w-16 px-1.5 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1681ba]"
                                />
                              ) : (
                                (() => {
                                  const effectiveDiscount = getLineEffectiveDiscount(order, item);
                                  return effectiveDiscount === null ? '—' : `${(effectiveDiscount * 100).toFixed(2)}%`;
                                })()
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                              {(() => {
                                const effectiveDiscount = getLineEffectiveDiscount(order, item);
                                const unitPrice = Number(item.precio_estimado_unitario) || 0;
                                const discountedUnitPrice = effectiveDiscount === null
                                  ? unitPrice
                                  : unitPrice * (1 - effectiveDiscount);
                                return formatMoney(discountedUnitPrice);
                              })()}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {formatMoney(getLineDiscountedTotal(order, item))}
                            </td>
                            <td className="px-4 py-3 text-center align-top">
                              {(() => {
                                const lineDueIso = pendingOrderLineDueDates[item.id] ?? dueDateValue;
                                const hasLineDueOverride = Boolean(pendingOrderLineDueDates[item.id]);
                                const overrideIso = pendingOrderLineDueDates[item.id];
                                const dueParts = overrideIso?.split('-') ?? [];
                                const lineDueLabel =
                                  overrideIso && dueParts.length === 3
                                    ? new Date(
                                      Number(dueParts[0]),
                                      Number(dueParts[1]) - 1,
                                      Number(dueParts[2])
                                    ).toLocaleDateString(undefined, {
                                      day: '2-digit',
                                      month: 'short',
                                      year: 'numeric'
                                    })
                                    : overrideIso ?? '';
                                return (
                                  <div className="flex flex-col items-center gap-0.5">
                                    <div className="flex items-center justify-center space-x-2">
                                      <button
                                        onClick={() => {
                                          setOpenLineDiscountEditor(prev => ({ ...prev, [item.id]: !prev[item.id] }));
                                          setLineDiscountInputs(prev => ({
                                            ...prev,
                                            [item.id]: item.discount === null || item.discount === undefined
                                              ? ''
                                              : (Number(item.discount) * 100).toFixed(2)
                                          }));
                                        }}
                                        className="inline-flex items-center justify-center w-8 h-8 text-[#1681ba] hover:bg-blue-100 rounded-full transition-colors"
                                        title={t('supply.pendingOrders.editLineDiscountTooltip')}
                                        aria-label={t('supply.pendingOrders.editLineDiscountTooltip')}
                                      >
                                        <Percent className="h-4 w-4" />
                                      </button>
                                      <span
                                        className={`relative inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors ${hasLineDueOverride
                                            ? 'text-amber-700 bg-amber-50 hover:bg-amber-100'
                                            : 'text-gray-600 hover:bg-gray-100'
                                          }`}
                                        title={t('supply.pendingOrders.lineDeliveryDateTooltip')}
                                      >
                                        <Calendar className="h-4 w-4 pointer-events-none" />
                                        <input
                                          type="date"
                                          className="absolute inset-0 cursor-pointer opacity-0 w-full h-full"
                                          value={lineDueIso}
                                          onChange={e => {
                                            const v = e.target.value;
                                            setPendingOrderLineDueDates(prev => {
                                              const next = { ...prev };
                                              if (v === dueDateValue) {
                                                delete next[item.id];
                                              } else {
                                                next[item.id] = v;
                                              }
                                              return next;
                                            });
                                          }}
                                        />
                                      </span>
                                      {/* Solo mostrar botón + si hay 1 item o menos del mismo producto */}
                                      {!isMultipleItems && (
                                        <button
                                          onClick={() => handleDuplicateItem(order.id, item.id)}
                                          disabled={duplicatingItems[item.id]}
                                          className="inline-flex items-center justify-center w-8 h-8 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-full transition-colors disabled:opacity-50"
                                          title={t('supply.pendingOrders.duplicateLineTooltip')}
                                        >
                                          {duplicatingItems[item.id] ? (
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                          ) : (
                                            <Plus className="h-4 w-4" />
                                          )}
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleDeleteProductFromOrder(order.id, item.id)}
                                        disabled={deletingProducts[item.id]}
                                        className="inline-flex items-center justify-center w-8 h-8 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-full transition-colors disabled:opacity-50"
                                        title={t('supply.pendingOrders.deleteLineTooltip')}
                                      >
                                        {deletingProducts[item.id] ? (
                                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                        ) : (
                                          <X className="h-4 w-4" />
                                        )}
                                      </button>
                                    </div>
                                    {hasLineDueOverride ? (
                                      <span className="text-[10px] leading-tight tabular-nums max-w-[5.5rem] font-medium text-amber-800">
                                        {lineDueLabel}
                                      </span>
                                    ) : null}
                                  </div>
                                );
                              })()}
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                  {/* Footer con total de la orden */}
                  <tfoot className="bg-gray-100">
                    <tr>
                      <td colSpan={5} className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                        {t('supply.pendingOrders.orderFooterTotal')}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-green-600">
                        {formatMoney(orderTotals.baseTotal)}
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-green-700">
                        {showDiscountedOrderTotal ? formatMoney(orderTotals.discountedTotal) : '—'}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {(() => {
                const totalItems = order.items.length;
                const pageSize = pendingOrderItemsPageSize;
                const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
                const currentPage = pendingOrderPages[order.id] ?? 1;
                const safePage = Math.min(Math.max(currentPage, 1), totalPages);
                return (
                  <TablePaginationBar
                    currentPage={safePage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    pageSize={pageSize}
                    onPageChange={(nextPage) =>
                      setPendingOrderPages(prev => ({ ...prev, [order.id]: nextPage }))
                    }
                    onPageSizeChange={(size) => {
                      setPendingOrderItemsPageSize(size);
                      setPendingOrderPages(prev => ({ ...prev, [order.id]: 1 }));
                    }}
                  />
                );
              })()}
            </div>
          );
        })}
      </div>
    ) : (
      <div className="text-center py-12">
        <Package className="mx-auto h-16 w-16 text-gray-300" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">{t('supply.pendingOrders.noOrders')}</h3>
        <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
          {t('supply.pendingOrders.noOrdersDesc')}
        </p>
      </div>
    )}
  </div>
  );
}
